import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, statoBadgeClass, statoLabel } from "../lib/api";
import { Plus, Search, Loader2, Pencil, Trash2, X, ArrowDownUp, FilterX, FileSpreadsheet, Upload, ClipboardCheck, CalendarClock, HardHat, Wrench, ShieldCheck, SlidersHorizontal, Send } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ExcelImportModal from "../components/ExcelImportModal";
import ClienteFormModal from "../components/ClienteFormModal";
import { useAuth } from "../context/AuthContext";
import { canDoAction, isFieldRole } from "../lib/menu";

const STATI = ["bozza", "in_lavorazione", "in_corso", "completato", "sospeso", "annullato"];

// Mappa colori semaforo (3 stati: empty/partial/done)
const PROGRESS_COLORS = {
  empty: { bg: "var(--surface-muted)", fg: "var(--text-muted)", border: "var(--border)", label: "Non iniziato" },
  partial: { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A", label: "In corso" },
  done: { bg: "#D1FAE5", fg: "#065F46", border: "#A7F3D0", label: "Completato" },
  ko: { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5", label: "KO installazione" },
  attesa: { bg: "#FFEDD5", fg: "#9A3412", border: "#FDBA74", label: "In attesa conferma fornitore" },
};

const DATE_FIELD_LABELS = {
  created: "Inserimento cliente",
  sopralluogo: "Chiusura sopralluogo",
  installazione: "Chiusura installazione",
  intervento: "Intervento effettuato",
};

const PROGRESS_SECTIONS = [
  { slug: "sopralluogo", label: "Sopralluogo", icon: ClipboardCheck },
  { slug: "pianificazione", label: "Pianificazione", icon: CalendarClock },
  { slug: "installazione", label: "Installazione", icon: HardHat },
  { slug: "intervento", label: "Interventi", icon: Wrench },
  { slug: "check_quality", label: "Check Quality", icon: ShieldCheck },
];

function ProgressDots({ progress, flusso, testid }) {
  const p = progress || {};
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      {PROGRESS_SECTIONS.map(({ slug, label, icon: Icon }) => {
        let status = p[slug] || "empty";
        // Override semaforo Installazione in base al flusso del cliente
        if (slug === "installazione") {
          if (flusso === "ko") status = "ko";
          else if (flusso === "attesa_conferma") status = "attesa";
        }
        const col = PROGRESS_COLORS[status] || PROGRESS_COLORS.empty;
        return (
          <span
            key={slug}
            title={`${label}: ${col.label}`}
            aria-label={`${label}: ${col.label}`}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full border"
            style={{ background: col.bg, color: col.fg, borderColor: col.border }}
            data-testid={`progress-${slug}-${status}`}
          >
            <Icon className="w-3 h-3" />
          </span>
        );
      })}
    </div>
  );
}

const SORT_OPTIONS = [
  { value: "created_desc", label: "Più recenti" },
  { value: "created_asc", label: "Meno recenti" },
  { value: "nome_asc", label: "Nome A-Z" },
  { value: "nome_desc", label: "Nome Z-A" },
  { value: "stato", label: "Stato pratica" },
];

export default function Clienti() {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const fx = showFilters ? "" : "hidden md:block";
  const fxGrid = showFilters ? "" : "hidden md:grid";
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clienti, setClienti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const [filterCommessa, setFilterCommessa] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [filterModuleActivity, setFilterModuleActivity] = useState(searchParams.get("module_activity") || "");
  const [filterDateField, setFilterDateField] = useState(searchParams.get("date_field") || "");
  const [filterDateFrom, setFilterDateFrom] = useState(searchParams.get("date_from") || "");
  const [filterDateTo, setFilterDateTo] = useState(searchParams.get("date_to") || "");
  const [filterProgressModule, setFilterProgressModule] = useState(searchParams.get("progress_module") || "");
  const [filterProgressStatus, setFilterProgressStatus] = useState(searchParams.get("progress_status") || "");
  const [daInviare, setDaInviare] = useState(searchParams.get("da_inviare") === "1");
  const [daInviareCount, setDaInviareCount] = useState(null);
  const [sortBy, setSortBy] = useState("created_desc");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const debounceRef = useRef(null);
  const firstLoad = useRef(true);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (filterCommessa) params.set("commessa_id", filterCommessa);
      if (filterStato) params.set("stato", filterStato);
      if (filterModuleActivity) params.set("module_activity", filterModuleActivity);
      if (filterDateField) params.set("date_field", filterDateField);
      if (filterDateFrom) params.set("date_from", filterDateFrom);
      if (filterDateTo) params.set("date_to", filterDateTo);
      if (filterProgressModule && filterProgressStatus) {
        params.set("progress_module", filterProgressModule);
        params.set("progress_status", filterProgressStatus);
      }
      if (sortBy) params.set("sort_by", sortBy);
      if (daInviare) params.set("da_inviare", "true");
      const { data } = await api.get(`/clienti?${params}`);
      api.get("/clienti-da-inviare/count").then((r) => setDaInviareCount(r.data.count)).catch(() => {});
      setClienti(data);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const loadCommesse = async () => {
    const { data } = await api.get("/commesse");
    setCommesse(data);
  };

  useEffect(() => {
    loadCommesse();
    fetchData();
    // eslint-disable-next-line
  }, []);

  // Debounced auto-fetch when filters change
  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return; }
    setSearching(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { fetchData(); }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [q, filterCommessa, filterStato, filterModuleActivity, filterDateField, filterDateFrom, filterDateTo, filterProgressModule, filterProgressStatus, sortBy, daInviare]);

  // Sync filterModuleActivity ↔ URL (so deep link from Dashboard works and clearing removes the param)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (filterModuleActivity) next.set("module_activity", filterModuleActivity); else next.delete("module_activity");
    if (filterDateField) next.set("date_field", filterDateField); else next.delete("date_field");
    if (filterDateFrom) next.set("date_from", filterDateFrom); else next.delete("date_from");
    if (filterDateTo) next.set("date_to", filterDateTo); else next.delete("date_to");
    if (daInviare) next.set("da_inviare", "1"); else next.delete("da_inviare");
    if (filterProgressModule && filterProgressStatus) {
      next.set("progress_module", filterProgressModule);
      next.set("progress_status", filterProgressStatus);
    } else {
      next.delete("progress_module");
      next.delete("progress_status");
    }
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [filterModuleActivity, filterDateFrom, filterDateTo, filterProgressModule, filterProgressStatus, daInviare]);

  const activeFilters = [
    q && "q",
    filterCommessa && "commessa",
    filterStato && "stato",
    filterModuleActivity && "modulo",
    (filterDateFrom || filterDateTo) && "data",
    (filterProgressModule && filterProgressStatus) && "avanzamento",
  ].filter(Boolean);
  const hasActiveFilters = activeFilters.length > 0;

  const clearFilters = () => {
    setQ("");
    setFilterCommessa("");
    setFilterStato("");
    setFilterModuleActivity("");
    setFilterDateField("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterProgressModule("");
    setFilterProgressStatus("");
  };

  const openNew = () => {
    if (commesse.length === 0) {
      toast.error("Crea prima una commessa");
      return;
    }
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setShowForm(true);
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare il cliente ${c.nome} ${c.cognome_ragione_sociale}?`)) return;
    try {
      await api.delete(`/clienti/${c.id}`);
      toast.success("Cliente eliminato");
      await fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportXlsx = async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (filterCommessa) params.set("commessa_id", filterCommessa);
      if (filterStato) params.set("stato", filterStato);
      if (filterModuleActivity) params.set("module_activity", filterModuleActivity);
      if (filterDateField) params.set("date_field", filterDateField);
      if (filterDateFrom) params.set("date_from", filterDateFrom);
      if (filterDateTo) params.set("date_to", filterDateTo);
      if (filterProgressModule && filterProgressStatus) {
        params.set("progress_module", filterProgressModule);
        params.set("progress_status", filterProgressStatus);
      }
      if (sortBy) params.set("sort_by", sortBy);
      const res = await api.get(`/clienti/export/xlsx?${params}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      a.href = url;
      a.download = `clienti_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Esportati ${clienti.length} clienti in Excel`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const commessaName = (id) => commesse.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="clienti-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Clienti</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }} data-testid="clienti-count">
            {loading ? "…" : `${clienti.length} ${clienti.length === 1 ? "cliente" : "clienti"}`}
            {hasActiveFilters && <span> · <span style={{ color: "var(--accent)" }}>{activeFilters.length} filtri attivi</span></span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isFieldRole(user) && (
          <button
            onClick={() => setShowImport(true)}
            className="btn-secondary"
            data-testid="import-xlsx-btn"
            title="Importa clienti da Excel"
          >
            <Upload className="w-4 h-4" /> Importa Excel
          </button>
          )}
          {!isFieldRole(user) && canDoAction(user, "export_data") && (
          <button
            onClick={exportXlsx}
            disabled={loading || clienti.length === 0}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-xlsx-btn"
            title="Esporta i clienti filtrati in formato Excel"
          >
            <FileSpreadsheet className="w-4 h-4" /> Esporta Excel
          </button>
          )}
          {!isFieldRole(user) && (
          <button onClick={openNew} className="btn-primary" data-testid="new-cliente-btn">
            <Plus className="w-4 h-4" /> Nuovo cliente
          </button>
          )}
        </div>
      </div>

      <div className="card-shell p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="relative md:col-span-5">
            <Search className="w-4 h-4 absolute left-3 top-3" style={{ color: "var(--text-muted)" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca nome, cognome, telefono, email, ID, città…"
              className="w-full pl-9 pr-9 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="cliente-search"
            />
            {searching && (
              <Loader2 className="w-4 h-4 absolute right-3 top-3 animate-spin" style={{ color: "var(--text-muted)" }} />
            )}
            {q && !searching && (
              <button
                onClick={() => setQ("")}
                className="absolute right-2 top-2 p-1 rounded hover:bg-black/5"
                data-testid="clear-search"
                aria-label="Pulisci ricerca"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {daInviareCount !== null && (
            <button type="button" onClick={() => setDaInviare((v) => !v)} data-testid="filter-da-inviare"
              className="md:col-span-12 inline-flex items-center gap-2 justify-center md:justify-start w-full md:w-fit px-3 py-2 text-sm font-semibold rounded-full border transition-colors"
              style={daInviare ? { background: "#0D47A1", color: "#fff", borderColor: "#0D47A1" } : { background: "#E3F2FD", color: "#0D47A1", borderColor: "#BBDEFB" }}>
              <Send className="w-4 h-4" /> Pronti da inviare alla commessa
              <span className="px-1.5 rounded-full text-xs" style={{ background: daInviare ? "#ffffff33" : "#0D47A1", color: "#fff" }} data-testid="filter-da-inviare-count">{daInviareCount}</span>
            </button>
          )}

          <button type="button" onClick={() => setShowFilters((v) => !v)} className="md:hidden btn-secondary justify-center w-full" data-testid="toggle-filters">
            <SlidersHorizontal className="w-4 h-4" /> {showFilters ? "Nascondi filtri" : "Filtri e ordinamento"}
          </button>

          <select
            value={filterCommessa}
            onChange={(e) => setFilterCommessa(e.target.value)}
            className={`${fx} md:col-span-3 px-3 py-2 text-sm rounded-md border outline-none`}
            style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="filter-commessa"
          >
            <option value="">Tutte le commesse</option>
            {commesse.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select
            value={filterStato}
            onChange={(e) => setFilterStato(e.target.value)}
            className={`${fx} md:col-span-2 px-3 py-2 text-sm rounded-md border outline-none`}
            style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="filter-stato"
          >
            <option value="">Tutti gli stati</option>
            {STATI.map((s) => <option key={s} value={s}>{statoLabel(s)}</option>)}
          </select>

          <div className={`${fx} md:col-span-2 relative`}>
            <ArrowDownUp className="w-4 h-4 absolute left-3 top-3 pointer-events-none" style={{ color: "var(--text-muted)" }} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-md border outline-none appearance-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="sort-by"
            >
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Riga 2: filtro avanzamento (sezione + stato) */}
        <div className={`${fxGrid} grid grid-cols-1 md:grid-cols-12 gap-3 items-end mt-3`}>
          <div className="md:col-span-3">
            <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              Avanzamento · Sezione
            </label>
            <select
              value={filterProgressModule}
              onChange={(e) => {
                const v = e.target.value;
                setFilterProgressModule(v);
                if (!v) setFilterProgressStatus("");
              }}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="filter-progress-module"
            >
              <option value="">Qualsiasi sezione</option>
              {PROGRESS_SECTIONS.map((s) => (
                <option key={s.slug} value={s.slug}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              Avanzamento · Stato
            </label>
            <select
              value={filterProgressStatus}
              onChange={(e) => setFilterProgressStatus(e.target.value)}
              disabled={!filterProgressModule}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none disabled:bg-[var(--surface-muted)] disabled:cursor-not-allowed"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="filter-progress-status"
            >
              <option value="">{filterProgressModule ? "Tutti gli stati" : "Scegli prima una sezione"}</option>
              <option value="empty">Non iniziato</option>
              <option value="partial">In corso</option>
              <option value="done">Completato</option>
            </select>
          </div>
          <div className="md:col-span-6 text-xs flex items-center" style={{ color: "var(--text-muted)" }}>
            {filterProgressModule && filterProgressStatus ? (
              <span>
                Mostra solo i clienti il cui modulo <b>{PROGRESS_SECTIONS.find((s) => s.slug === filterProgressModule)?.label}</b>{" "}
                è in stato <b>{filterProgressStatus === "empty" ? "Non iniziato" : filterProgressStatus === "partial" ? "In corso" : "Completato"}</b>.
              </span>
            ) : (
              <span>Filtra i clienti in base allo stato di una sezione operativa.</span>
            )}
          </div>
        </div>

        {/* Riga 3: filtro per data (inserimento / chiusura moduli) */}
        <div className={`${fxGrid} grid grid-cols-1 md:grid-cols-12 gap-3 items-end mt-3`}>
          <div className="md:col-span-3">
            <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              Filtra per data di…
            </label>
            <select
              value={filterDateField}
              onChange={(e) => setFilterDateField(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="filter-date-field"
            >
              <option value="">Nessun filtro data</option>
              <option value="created">Inserimento cliente</option>
              <option value="sopralluogo">Chiusura sopralluogo</option>
              <option value="installazione">Chiusura installazione</option>
              <option value="intervento">Intervento effettuato</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Dal</label>
            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} disabled={!filterDateField}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none disabled:bg-[var(--surface-muted)] disabled:cursor-not-allowed"
              style={{ borderColor: "var(--border)", background: "#fff" }} data-testid="filter-date-from" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Al</label>
            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} disabled={!filterDateField}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none disabled:bg-[var(--surface-muted)] disabled:cursor-not-allowed"
              style={{ borderColor: "var(--border)", background: "#fff" }} data-testid="filter-date-to" />
          </div>
          <div className="md:col-span-5 text-xs flex items-center" style={{ color: "var(--text-muted)" }}>
            {filterDateField ? (
              <span>
                Clienti per data di <b>{DATE_FIELD_LABELS[filterDateField]}</b>{filterDateField !== "created" && " (modulo completato al 100%)"} — utile per riscontrare il fatturato del periodo.
              </span>
            ) : (
              <span>Scegli un tipo di data per filtrare (es. chiusura sopralluogo) e verificare il fatturato.</span>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 items-center mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Filtri attivi:</span>
            {q && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full" style={{ background: "var(--surface-muted)" }} data-testid="chip-q">
                Ricerca: "{q}"
                <button onClick={() => setQ("")} className="hover:opacity-70"><X className="w-3 h-3" /></button>
              </span>
            )}
            {filterCommessa && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full" style={{ background: "var(--surface-muted)" }} data-testid="chip-commessa">
                Commessa: {commessaName(filterCommessa)}
                <button onClick={() => setFilterCommessa("")} className="hover:opacity-70"><X className="w-3 h-3" /></button>
              </span>
            )}
            {filterStato && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full" style={{ background: "var(--surface-muted)" }} data-testid="chip-stato">
                Stato: {statoLabel(filterStato)}
                <button onClick={() => setFilterStato("")} className="hover:opacity-70"><X className="w-3 h-3" /></button>
              </span>
            )}
            {filterModuleActivity && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full font-semibold"
                style={{ background: "#E6F4EA", color: "#1B5E20" }}
                data-testid="chip-module-activity"
              >
                Attività: {filterModuleActivity.charAt(0).toUpperCase() + filterModuleActivity.slice(1)}
                <button onClick={() => setFilterModuleActivity("")} className="hover:opacity-70"><X className="w-3 h-3" /></button>
              </span>
            )}
            {filterDateField && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full font-semibold"
                style={{ background: "#E6F4EA", color: "#1B5E20" }}
                data-testid="chip-date-field"
              >
                Data: {DATE_FIELD_LABELS[filterDateField]}
                <button onClick={() => setFilterDateField("")} className="hover:opacity-70"><X className="w-3 h-3" /></button>
              </span>
            )}
            {(filterDateFrom || filterDateTo) && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full font-semibold"
                style={{ background: "#E6F4EA", color: "#1B5E20" }}
                data-testid="chip-date-range"
              >
                {filterDateFrom && filterDateTo
                  ? `${filterDateFrom} → ${filterDateTo}`
                  : filterDateFrom
                    ? `Da ${filterDateFrom}`
                    : `Fino al ${filterDateTo}`}
                <button onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); }} className="hover:opacity-70"><X className="w-3 h-3" /></button>
              </span>
            )}
            {filterProgressModule && filterProgressStatus && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full font-semibold"
                style={{ background: "#E6F4EA", color: "#1B5E20" }}
                data-testid="chip-progress"
              >
                {PROGRESS_SECTIONS.find((s) => s.slug === filterProgressModule)?.label}:{" "}
                {filterProgressStatus === "empty" ? "Non iniziato" : filterProgressStatus === "partial" ? "In corso" : "Completato"}
                <button
                  onClick={() => { setFilterProgressModule(""); setFilterProgressStatus(""); }}
                  className="hover:opacity-70"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button
              onClick={clearFilters}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold hover:underline"
              style={{ color: "var(--accent)" }}
              data-testid="clear-filters"
            >
              <FilterX className="w-3.5 h-3.5" /> Cancella tutto
            </button>
          </div>
        )}
      </div>

      <div className="card-shell overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento…</div>
        ) : clienti.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="no-results">
              {hasActiveFilters ? "Nessun cliente trovato con i filtri attuali" : "Nessun cliente trovato"}
            </div>
            {hasActiveFilters ? (
              <button onClick={clearFilters} className="btn-secondary mt-4" data-testid="clear-filters-empty">
                <FilterX className="w-4 h-4" /> Cancella filtri
              </button>
            ) : (
              <button onClick={openNew} className="btn-primary mt-4">Crea il primo cliente</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--surface-muted)" }}>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>ID</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Cliente</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Contatti</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Commessa</th>
                  {filterDateField && (
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold whitespace-nowrap" style={{ color: "#1B5E20" }}>{DATE_FIELD_LABELS[filterDateField]}</th>
                  )}
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold whitespace-nowrap" style={{ color: "var(--text-muted)" }}>Avanzamento</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Stato</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {clienti.map((c) => (
                  <tr key={c.id} data-tap className="border-t hover:bg-[var(--surface-muted)] cursor-pointer" style={{ borderColor: "var(--border)" }} data-testid={`cliente-row-${c.id}`}
                    onClick={(e) => { if (!e.target.closest("a,button,input,select,label")) navigate(`/clienti/${c.id}`); }}>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--text-muted)" }}>{c.cliente_id}</td>
                    <td className="px-4 py-3">
                      <Link to={`/clienti/${c.id}`} className="font-semibold hover:underline" data-testid={`cliente-link-${c.id}`}>
                        {c.nome} {c.cognome_ragione_sociale}
                      </Link>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>{c.citta} {c.provincia && `(${c.provincia})`}</div>
                      {(c.da_inviare || []).map((m) => (
                        <span key={m} className="inline-flex items-center gap-1 mt-1 mr-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: "#E3F2FD", color: "#0D47A1" }} data-testid={`da-inviare-${m}-${c.id}`}>
                          <Send className="w-3 h-3" /> {m} da inviare
                        </span>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{c.telefono || "—"}</div>
                      <div style={{ color: "var(--text-muted)" }}>{c.email || "—"}</div>
                    </td>
                    <td className="px-4 py-3">{commessaName(c.commessa_id)}</td>
                    {filterDateField && (
                      <td className="px-4 py-3 text-xs font-semibold whitespace-nowrap" style={{ color: "#1B5E20" }} data-testid={`data-rif-${c.id}`}>
                        {c.data_riferimento ? c.data_riferimento.split("-").reverse().join("/") : "—"}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <ProgressDots progress={c.progress} flusso={c.flusso_installazione} testid={`progress-${c.id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={statoBadgeClass(c.stato_pratica)}>{statoLabel(c.stato_pratica)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!isFieldRole(user) && (
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-black/5" data-testid={`edit-${c.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      )}
                      {!isFieldRole(user) && canDoAction(user, "clienti_delete") && (
                      <button onClick={() => remove(c)} className="p-1.5 rounded hover:bg-black/5 ml-1" data-testid={`delete-${c.id}`}>
                        <Trash2 className="w-4 h-4" style={{ color: "#B30000" }} />
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <ClienteFormModal
          open={showForm}
          cliente={editing}
          commesse={commesse}
          onClose={() => setShowForm(false)}
          onSaved={() => fetchData()}
        />
      )}

      <ExcelImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => fetchData()}
        resource="clienti"
      />
    </div>
  );
}
